import { useCallback } from "react"
import { useMutation } from "urql"

import { GL_ACCOUNT_ERROR_MESSAGES } from "../constants/errorMessages"

import { CreateGlAccountDocument } from "@/graphql/codegen/graphql"
import type { GlAccountUpsertInput } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import type { ActionOutcome } from "@/types/common"

interface UseCreateGlAccountResult {
  createGlAccount: (input: GlAccountUpsertInput) => Promise<ActionOutcome>
  isCreating: boolean
}

export const useCreateGlAccount = (): UseCreateGlAccountResult => {
  const [{ fetching }, executeMutation] = useMutation(CreateGlAccountDocument)
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const createGlAccount = useCallback(
    async (input: GlAccountUpsertInput): Promise<ActionOutcome> => {
      const result = await executeMutation({ input })

      if (result.error) {
        const errorMessage = result.error.message || "Failed to create GL account"

        if (errorMessage.includes(GL_ACCOUNT_ERROR_MESSAGES.ALREADY_EXISTS)) {
          return { success: false, error: new Error(errorMessage) }
        }

        notify({
          type: NotificationType.Error,
          silent: false,
          message: errorMessage,
        })
        return { success: false }
      }

      if (result.data?.glAccount.create.__typename === "GlAccountFailure") {
        const errorMessage = result.data.glAccount.create.error.message || "Failed to create GL account"

        if (errorMessage.includes(GL_ACCOUNT_ERROR_MESSAGES.ALREADY_EXISTS)) {
          return { success: false, error: new Error(errorMessage) }
        }

        notify({
          type: NotificationType.Error,
          silent: false,
          message: errorMessage,
        })
        return { success: false }
      }

      notify({
        type: NotificationType.Success,
        silent: false,
        message: "Account successfully created",
      })
      return { success: true }
    },
    [executeMutation, notify]
  )

  return {
    createGlAccount,
    isCreating: fetching,
  }
}
